import { alpha, Box, Divider, Grid, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { formatCurrency } from 'components/DealsKanban/aggregateStageTotals'
import ActiveCountCell from 'components/MTB/grid/cells/ActiveCountCell'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import format from 'date-fns/format'
import React from 'react'
import { Product } from 'types'
import { renderColorByString } from '../DataGrid/renderColorByString'

function formatDate(timestamp) {
  if (!timestamp) return
  return format(new Date(timestamp), 'MM/dd/yyyy')
}

type ProductTooltipTitleProps = {
  activeProducts: Product[]
}

const ProductTooltipTitle: React.FC<ProductTooltipTitleProps> = ({ activeProducts }) => (
  <Stack>
    {activeProducts?.map((p: Product) => (
      <Box
        key={p?.SK}
        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
      >
        <Typography
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
            textAlign: 'left',
          }}
        >
          {p?.productName}
        </Typography>
        <Typography width={'45%'} maxWidth={'45%'} sx={{ color: 'text.success', textAlign: 'right' }}>
          {formatCurrency(p?.price)}
        </Typography>
      </Box>
    ))}
  </Stack>
)
const MtbGridHeader = ({ organization }) => {
  const theme = useTheme()
  const densityStyles = getDensityStyles('compact')
  const params = { row: { isParent: true, activeCount: organization?.activeCount } } as GridRenderCellParams
  const activeProducts = organization?.products
    ?.filter(p => p?.status === 'Active')
    ?.sort((a, b) => {
      const aDate = a.endDate ? new Date(a.endDate).getTime() : 0
      const bDate = b.endDate ? new Date(b.endDate).getTime() : 0
      return aDate - bDate
    })

  const activeProductsCount = activeProducts?.length
  const activeStartDates = activeProducts?.map(p => new Date(p?.startDate).getTime()).filter(Boolean)
  const activeEndDates = activeProducts?.map(p => new Date(p?.endDate).getTime()).filter(Boolean)

  const earliestStart = activeStartDates?.length ? new Date(Math.min(...activeStartDates)) : null
  const latestEnd = activeEndDates?.length ? new Date(Math.max(...activeEndDates)) : null

  return (
    <Box
      sx={{
        p: 3,
        // backgroundColor: theme.palette.background.default,
        borderRadius: '8px',
        boxShadow: `0 2px 6px ${alpha(theme.palette.text.primary, 0.1)}`,
      }}
    >
      <Grid
        container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          overflowY: 'hidden',
        }}
      >
        <Grid item xs={12} sm={12} md={12} lg={3} xl={3} sx={{ py: 1, px: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left', width: '100%' }}>
              Active Deals
            </Typography>
            <Box>
              <ActiveCountCell params={params} densityStyles={densityStyles} />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Earliest Start Date
            </Typography>
            <Typography sx={{ textAlign: 'left', color: theme.palette.text.primary }}>
              {earliestStart ? formatDate(earliestStart) : '—'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Latest End Date
            </Typography>
            <Typography sx={{ textAlign: 'left', color: theme.palette.text.primary }}>
              {latestEnd ? formatDate(latestEnd) : '—'}
            </Typography>
          </Box>
        </Grid>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Grid item xs={12} sm={12} md={12} lg={3} xl={3} sx={{ py: 1, px: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Onboarding Status
            </Typography>
            <Typography sx={{ color: renderColorByString(organization?.onboardingStatus ?? 'On Hold') }}>
              {organization?.onboardingStatus ?? 'On Hold'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Master Status
            </Typography>
            <Typography sx={{ color: renderColorByString(organization?.masterStatus) }}>
              {organization?.masterStatus || 'no nco status'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'default',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Active Products
            </Typography>
            <Tooltip title={<ProductTooltipTitle activeProducts={activeProducts} />}>
              <Typography
                sx={{
                  color: 'text.highlight',
                  ':hover': {
                    opacity: '0.8',
                  },
                }}
              >
                {activeProductsCount}
              </Typography>
            </Tooltip>
          </Box>
        </Grid>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Grid item xs={12} sm={12} md={12} lg={3} xl={3} sx={{ py: 1, px: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Organization Status
            </Typography>
            <Typography sx={{ color: renderColorByString(organization?.status) }}>{organization?.status}</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Website
            </Typography>
            <Typography
              sx={{ height: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12 }}
            >
              {organization?.website ?? '—'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" color="textSecondary" sx={{ textAlign: 'left' }}>
              Monthly Total
            </Typography>
            <Typography sx={{ color: 'text.success' }}>
              {formatCurrency(organization?.monthlyContractAmount) ?? `$0`}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default MtbGridHeader
